import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useTheme,
} from "@mui/material";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
  Filler,
  TooltipItem,
} from "chart.js";
import { Chart } from "react-chartjs-2";
import {
  analyticsAPI,
  CashflowCoverage,
  CashflowMonth,
  CashflowResponse,
} from "../services/api/analyticsApi";
import { plaidService, PlaidAccount } from "../services/external/plaidService";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  ChartTooltip,
  Legend,
  Filler
);

type RangeKey = "12" | "24" | "36" | "60" | "all";

const RANGE_OPTIONS: { key: RangeKey; label: string; months?: number }[] = [
  { key: "12", label: "1 year", months: 12 },
  { key: "24", label: "2 years", months: 24 },
  { key: "36", label: "3 years", months: 36 },
  { key: "60", label: "5 years", months: 60 },
  { key: "all", label: "All data" },
];

const usd = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  maximumFractionDigits: 0,
});
const fmt = (n: number) => usd.format(n);

const monthLabel = (ym: string) => {
  const [y, m] = ym.split("-").map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString("en-US", {
    month: "short",
    year: "2-digit",
  });
};

const trailingAverage = (values: number[], window: number) =>
  values.map((_, i) => {
    const slice = values.slice(Math.max(0, i - window + 1), i + 1);
    return slice.reduce((a, b) => a + b, 0) / slice.length;
  });

const ROLE_LABEL: Record<CashflowCoverage["role"], string> = {
  bank: "Bank",
  card: "Credit card",
  wallet: "Wallet",
};

interface YearRow {
  year: string;
  income: number;
  expenses: number;
  net: number;
  months: number;
}

const summariseYears = (months: CashflowMonth[]): YearRow[] => {
  const byYear = new Map<string, YearRow>();
  for (const m of months) {
    const year = m.month.slice(0, 4);
    const row = byYear.get(year) || {
      year,
      income: 0,
      expenses: 0,
      net: 0,
      months: 0,
    };
    row.income += m.income;
    row.expenses += m.expenses;
    row.net += m.net;
    if (m.transactions > 0) row.months += 1;
    byYear.set(year, row);
  }
  return [...byYear.values()].sort((a, b) => b.year.localeCompare(a.year));
};

const summariseChannels = (months: CashflowMonth[]) => {
  const totals = new Map<string, number>();
  for (const m of months) {
    for (const [channel, amount] of Object.entries(m.incomeByChannel)) {
      totals.set(channel, (totals.get(channel) || 0) + amount);
    }
  }
  const all = [...totals.values()].reduce((a, b) => a + b, 0);
  return [...totals.entries()]
    .map(([channel, amount]) => ({
      channel,
      amount,
      share: all > 0 ? amount / all : 0,
    }))
    .sort((a, b) => b.amount - a.amount);
};

const StatTile: React.FC<{
  label: string;
  value: string;
  hint?: string;
  color?: string;
}> = ({ label, value, hint, color }) => (
  <Card variant="outlined" sx={{ height: "100%" }}>
    <CardContent>
      <Typography variant="overline" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="h5" sx={{ fontWeight: 600, color }}>
        {value}
      </Typography>
      {hint && (
        <Typography variant="caption" color="text.secondary">
          {hint}
        </Typography>
      )}
    </CardContent>
  </Card>
);

const TrendsPage: React.FC = () => {
  const theme = useTheme();
  const [range, setRange] = useState<RangeKey>("36");
  const [data, setData] = useState<CashflowResponse | null>(null);
  const [accounts, setAccounts] = useState<PlaidAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showMethod, setShowMethod] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const option = RANGE_OPTIONS.find((o) => o.key === range)!;
    setLoading(true);
    setError(null);
    analyticsAPI
      .getCashflow(
        option.months ? { months: option.months } : { start: "2000-01-01" }
      )
      .then((result) => {
        if (!cancelled) setData(result);
      })
      .catch((err: unknown) => {
        if (!cancelled)
          setError(err instanceof Error ? err.message : "Failed to load");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [range]);

  useEffect(() => {
    let cancelled = false;
    plaidService
      .getAccountsCached()
      .then((list) => {
        if (!cancelled) setAccounts(list);
      })
      .catch(() => {
        /* account labels are a nicety; the trajectory still renders */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const accountById = useMemo(() => {
    const map = new Map<string, PlaidAccount>();
    for (const a of accounts) map.set(a.account_id, a);
    return map;
  }, [accounts]);

  const months = useMemo(() => data?.months ?? [], [data]);
  const years = useMemo(() => summariseYears(months), [months]);
  const channels = useMemo(() => summariseChannels(months), [months]);

  const activeMonths = months.filter((m) => m.transactions > 0).length;
  const avgIncome = activeMonths
    ? (data?.totals.income ?? 0) / activeMonths
    : 0;
  const avgExpenses = activeMonths
    ? (data?.totals.expenses ?? 0) / activeMonths
    : 0;

  const incomeColor = theme.palette.success.main;
  const expenseColor = theme.palette.error.main;

  const chartData = useMemo(() => {
    const labels = months.map((m) => monthLabel(m.month));
    const income = months.map((m) => m.income);
    const expenses = months.map((m) => m.expenses);
    return {
      labels,
      datasets: [
        {
          type: "bar" as const,
          label: "Income",
          data: income,
          backgroundColor: incomeColor + "99",
          borderColor: incomeColor,
          borderWidth: 1,
          order: 2,
        },
        {
          type: "bar" as const,
          label: "Expenses",
          data: expenses,
          backgroundColor: expenseColor + "99",
          borderColor: expenseColor,
          borderWidth: 1,
          order: 2,
        },
        {
          type: "line" as const,
          label: "Income (3-mo avg)",
          data: trailingAverage(income, 3),
          borderColor: incomeColor,
          borderWidth: 2,
          pointRadius: 0,
          tension: 0.3,
          order: 1,
        },
        {
          type: "line" as const,
          label: "Expenses (3-mo avg)",
          data: trailingAverage(expenses, 3),
          borderColor: expenseColor,
          borderWidth: 2,
          borderDash: [6, 4],
          pointRadius: 0,
          tension: 0.3,
          order: 1,
        },
      ],
    };
  }, [months, incomeColor, expenseColor]);

  const chartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: "index" as const, intersect: false },
      plugins: {
        legend: {
          position: "bottom" as const,
          labels: { color: theme.palette.text.secondary },
        },
        tooltip: {
          callbacks: {
            label: (ctx: TooltipItem<"bar" | "line">) =>
              `${ctx.dataset.label}: ${fmt(ctx.parsed.y ?? 0)}`,
          },
        },
      },
      scales: {
        x: {
          ticks: {
            color: theme.palette.text.secondary,
            maxRotation: 0,
            autoSkip: true,
          },
          grid: { display: false },
        },
        y: {
          ticks: {
            color: theme.palette.text.secondary,
            callback: (value: number | string) => fmt(Number(value)),
          },
          grid: { color: theme.palette.divider },
        },
      },
    }),
    [theme]
  );

  const coverageLabel = (c: CashflowCoverage) => {
    const acct = accountById.get(c.accountId);
    const name = c.name || acct?.name || c.accountId.slice(0, 8);
    const institution =
      c.institutionName || acct?.institution?.name || undefined;
    const mask = acct?.mask ? ` ••${acct.mask}` : "";
    return { name: `${name}${mask}`, institution };
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        spacing={2}
        sx={{ mb: 3 }}
      >
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 600 }}>
            Trends
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Income and expenses by month across every linked and imported
            account, with transfers between your own accounts removed.
          </Typography>
        </Box>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={range}
          onChange={(_, v: RangeKey | null) => v && setRange(v)}
        >
          {RANGE_OPTIONS.map((o) => (
            <ToggleButton key={o.key} value={o.key}>
              {o.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading && !data ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress />
        </Box>
      ) : data ? (
        <Stack spacing={3} sx={{ opacity: loading ? 0.6 : 1 }}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 6, md: 3 }}>
              <StatTile
                label="Income"
                value={fmt(data.totals.income)}
                hint={`${fmt(avgIncome)} / month`}
                color={incomeColor}
              />
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
              <StatTile
                label="Expenses"
                value={fmt(data.totals.expenses)}
                hint={`${fmt(avgExpenses)} / month`}
                color={expenseColor}
              />
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
              <StatTile
                label="Net"
                value={fmt(data.totals.net)}
                hint={
                  data.totals.income > 0
                    ? `${Math.round(
                        (data.totals.net / data.totals.income) * 100
                      )}% of income kept`
                    : undefined
                }
                color={
                  data.totals.net >= 0
                    ? theme.palette.text.primary
                    : expenseColor
                }
              />
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
              <StatTile
                label="Months with data"
                value={`${activeMonths} / ${months.length}`}
                hint={
                  months.length
                    ? `${monthLabel(months[0].month)} – ${monthLabel(
                        months[months.length - 1].month
                      )}`
                    : undefined
                }
              />
            </Grid>
          </Grid>

          <Card variant="outlined">
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Monthly income vs. expenses
              </Typography>
              <Box sx={{ height: { xs: 280, md: 380 } }}>
                <Chart type="bar" data={chartData} options={chartOptions} />
              </Box>
            </CardContent>
          </Card>

          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 7 }}>
              <Card variant="outlined" sx={{ height: "100%" }}>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 1 }}>
                    By year
                  </Typography>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Year</TableCell>
                        <TableCell align="right">Income</TableCell>
                        <TableCell align="right">Expenses</TableCell>
                        <TableCell align="right">Net</TableCell>
                        <TableCell align="right">Months</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {years.map((y) => (
                        <TableRow key={y.year}>
                          <TableCell>{y.year}</TableCell>
                          <TableCell align="right" sx={{ color: incomeColor }}>
                            {fmt(y.income)}
                          </TableCell>
                          <TableCell align="right" sx={{ color: expenseColor }}>
                            {fmt(y.expenses)}
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{
                              fontWeight: 600,
                              color: y.net < 0 ? expenseColor : undefined,
                            }}
                          >
                            {fmt(y.net)}
                          </TableCell>
                          <TableCell align="right">{y.months}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, md: 5 }}>
              <Card variant="outlined" sx={{ height: "100%" }}>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 1 }}>
                    Income by channel
                  </Typography>
                  <Table size="small">
                    <TableBody>
                      {channels.map((c) => (
                        <TableRow key={c.channel}>
                          <TableCell>{c.channel}</TableCell>
                          <TableCell align="right">{fmt(c.amount)}</TableCell>
                          <TableCell align="right" sx={{ width: 64 }}>
                            {Math.round(c.share * 100)}%
                          </TableCell>
                        </TableRow>
                      ))}
                      {channels.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={3}>
                            <Typography variant="body2" color="text.secondary">
                              No income in this window.
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Card variant="outlined">
            <CardContent>
              <Typography variant="h6" sx={{ mb: 0.5 }}>
                Data coverage
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                How far back each account's history goes. Plaid links give up to
                two years at link time; older history comes from CSV or Direct
                Connect imports.
              </Typography>
              <Box sx={{ overflowX: "auto" }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Account</TableCell>
                      <TableCell>Role</TableCell>
                      <TableCell>Plaid</TableCell>
                      <TableCell>Imported</TableCell>
                      <TableCell align="right">Transactions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.coverage.map((c) => {
                      const label = coverageLabel(c);
                      return (
                        <TableRow key={c.accountId}>
                          <TableCell>
                            <Typography variant="body2">
                              {label.name}
                            </Typography>
                            {label.institution && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {label.institution}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={ROLE_LABEL[c.role]}
                              variant="outlined"
                              color={
                                c.role === "wallet" ? "default" : "primary"
                              }
                            />
                          </TableCell>
                          <TableCell sx={{ whiteSpace: "nowrap" }}>
                            {c.plaid ? `${c.plaid.from} → ${c.plaid.to}` : "—"}
                          </TableCell>
                          <TableCell sx={{ whiteSpace: "nowrap" }}>
                            {c.manual
                              ? `${c.manual.from} → ${c.manual.to}`
                              : "—"}
                          </TableCell>
                          <TableCell align="right">
                            {c.transactions.toLocaleString()}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </Box>
            </CardContent>
          </Card>

          <Card variant="outlined">
            <CardContent>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                onClick={() => setShowMethod((v) => !v)}
                sx={{ cursor: "pointer" }}
              >
                <Typography variant="h6">How this is calculated</Typography>
                <Typography variant="body2" color="primary">
                  {showMethod ? "Hide" : "Show"}
                </Typography>
              </Stack>
              <Collapse in={showMethod}>
                <Box component="ul" sx={{ pl: 2, mt: 1, mb: 2 }}>
                  {data.method.map((line) => (
                    <Typography component="li" variant="body2" key={line}>
                      {line}
                    </Typography>
                  ))}
                </Box>
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                  Excluded in this window
                </Typography>
                <Table size="small" sx={{ maxWidth: 480 }}>
                  <TableBody>
                    <TableRow>
                      <TableCell>Transfers between your accounts</TableCell>
                      <TableCell align="right">
                        {fmt(data.excluded.internalTransfers)}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Payments to tracked credit cards</TableCell>
                      <TableCell align="right">
                        {fmt(data.excluded.cardPayments)}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Wallet activity (PayPal / Venmo)</TableCell>
                      <TableCell align="right">
                        {fmt(data.excluded.walletActivity)}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Pending transactions</TableCell>
                      <TableCell align="right">
                        {fmt(data.excluded.pending)}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Imported rows duplicating Plaid</TableCell>
                      <TableCell align="right">
                        {data.excluded.duplicateImports.toLocaleString()}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </Collapse>
            </CardContent>
          </Card>
        </Stack>
      ) : null}
    </Box>
  );
};

export default TrendsPage;
